import { ToastrService } from 'ngx-toastr';
import { EMPTY, Observable, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { IVertoFxRateData, IVertoFxRateResponse } from '@app/core/models/client/products/products.model';
import { ChangeDetectionStrategy, Component, EventEmitter, Output, Input, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SlideInRightModalComponent } from '@app/shared/ui/slide-in-right-modal/slide-in-right-modal.component';
import { InputComponent } from '@app/shared/ui/input/input.component';
import { InputLabelComponent } from '@app/shared/ui/input-label/input-label.component';
import { AbstractControl, FormBuilder, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { BaseSelectDirective } from '@app/shared/directives/base-select.directive';
import { ButtonDirective } from '@app/shared/directives/button.directive';
import { ProductService } from '@app/core/services/client/product.service';
import { Select, Store } from '@ngxs/store';
import { AuthSelectors } from '@app/auth/store/auth.selectors';
import { LoadingService } from '@app/core/services/loading.service';
import { PortfolioService } from '@app/core/services/client/portfolio.service';
import { AuthActions } from '@app/auth/store/auth.actions';
import { SpinDirective } from '@app/shared/directives/spin.directive';
import { IDirectDebitAccount } from '@app/auth/store/auth.state.models';


@Component({
  selector: 'app-mutual-fund-order',
  standalone: true,
  imports: [
    CommonModule,
    SlideInRightModalComponent,
    InputComponent,
    InputLabelComponent,
    ReactiveFormsModule,
    FormsModule,
    ButtonDirective,
    SpinDirective,
    BaseSelectDirective
  ],
  templateUrl: './mutual-fund-order.component.html',
  styles: ``,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MutualFundOrderComponent {
  @Output() action = new EventEmitter<string>();
  @Input() product!: any;
  @Select(AuthSelectors.getLoadingStatus) loading$!: Observable<boolean>;



  fb = inject(FormBuilder);
  productService = inject(ProductService);
  store = inject(Store);
  change = inject(ChangeDetectorRef);
  toast = inject(ToastrService);
  loadingService = inject(LoadingService);
  portfolioService = inject(PortfolioService);
  router = inject(Router);
  walletBalance: number = 0
  returnPathUrl!: string;
  portfolioCash: number = 0;
  accountId: any = this.store.selectSnapshot(AuthSelectors.getActiveAccountID);
  activeAccount$ = this.store.select(AuthSelectors.getActiveAccount);
  walletBalance$ = this.store.select(AuthSelectors.getWalletBalance);
  unitsCalculated: number = 0;
  amount: number = 0;
  subs: Subscription[] = [];
  unitPrice: number = 100;
  linkedDirectDebitAccounts: IDirectDebitAccount[] = [];
  fxRateData: IVertoFxRateData | null = null;


  productOrderForm = this.fb.nonNullable.group({
    product_id: [this.product?.id],
    amount: ['', Validators.required],
    account_id: [this.accountId],
    payment_source: ['wallet', Validators.required],
    direct_debit_account_id: [''],
  });



  ngOnInit() {
    this.getDirectDebitAccounts();
    this.watchPaymentSource();
    const formValues: any = {
      product_id: this.product.id,

    };
    this.productOrderForm.patchValue(formValues);
    this.productOrderForm.get('amount')?.valueChanges.subscribe(() => {
      this.updateUnits();
    });
    if (this.product?.currency === 'USD') {
      this.watchAmountForFxRate();
    }
    const sub = this.walletBalance$.subscribe(balance => this.walletBalance = balance || 0);
    this.subs.push(sub)
  }


  getDirectDebitAccounts() {
    const sub = this.activeAccount$.subscribe((account) => {
      const mandates: IDirectDebitAccount[] = account?.directDebitMandates || [];
      this.linkedDirectDebitAccounts = mandates.filter(m => m.status === 'active');
      this.change.detectChanges();
    });
    this.subs.push(sub);
  }

  watchPaymentSource() {
    this.productOrderForm.get('payment_source')?.valueChanges.subscribe((val) => {
      if (val === 'direct_debit') {
        if (this.linkedDirectDebitAccounts.length === 0) {
          this.action.emit("all");
          this.router.navigate(['/settings/direct-debit']);
        } else {
          this.productOrderForm.get('direct_debit_account_id')?.setValidators(Validators.required);
          this.productOrderForm.get('direct_debit_account_id')?.updateValueAndValidity();
        }
      } else {
        this.productOrderForm.get('direct_debit_account_id')?.clearValidators();
        this.productOrderForm.get('direct_debit_account_id')?.updateValueAndValidity();
      }
    });
  }

  

  reduceBalance(amount:any, walletBalance$:number):number{
    const balance = walletBalance$ - amount;
    return balance;
  }



  updateUnits() {
    const amount = Number(this.productOrderForm.get('amount')?.value) || 0;
    // const unit = this.productOrderForm.get('unit')?.value;
    // const unitPrice = 100;       
    this.unitsCalculated = Math.floor(amount / this.unitPrice);

  }

  watchAmountForFxRate() {
    const sub = this.productOrderForm.get('amount')!.valueChanges.pipe(
      tap(() => {
        this.fxRateData = null;
        this.change.detectChanges();
      }),
      debounceTime(600),
      distinctUntilChanged(),
      switchMap((value) => {
        const amount = Number(value);
        if (!amount || amount <= 0) return EMPTY;
        return this.productService.getVertoFxRate({
          account_id: this.accountId,
          amount: String(amount),
          currency_from: 'USD',
          currency_to: 'NGN',
        });
      })
    ).subscribe({
      next: (res: IVertoFxRateResponse) => {
        this.fxRateData = res?.data ?? null;
        this.change.detectChanges();
      },
      error: () => {
        this.fxRateData = null;
        this.change.detectChanges();
      },
    });
    this.subs.push(sub);
  }

  closeModal(): void {
    this.action.emit();
  }





  createOrder() {
     this.store.dispatch(new AuthActions.SetLoadingStatus(true));
    if (this.productOrderForm.invalid) {
      this.toast.error('Please fill out the form correctly');
      return;
    }

    const { amount, payment_source, direct_debit_account_id, ...rest } = this.productOrderForm.getRawValue();
    const payload: any = {
      amount: Number(amount) * 100,
      ...rest,
      ...(payment_source === 'direct_debit' && direct_debit_account_id
        ? { direct_debit_mandate_id: direct_debit_account_id }
        : {}),
      ...(this.product?.currency === 'USD' && this.fxRateData
        ? {
            verto_fx_rate_id: this.fxRateData.id,
            naira_equivalent: Math.round(parseFloat(this.fxRateData.naira_equivalent) * 100).toString(),
          }
        : {}),
    };
    const sub = this.productService.createMutualFundOrder(this.accountId, payload).subscribe({
      next: (res: any) => {
        this.store.dispatch(new AuthActions.SetLoadingStatus(false));
        this.toast.success('Order created successfully');
        this.store.dispatch(AuthActions.GetActiveAccountDetails);
        this.closeModal();
        this.change.detectChanges();
      },
      error: () => {
        this.store.dispatch(new AuthActions.SetLoadingStatus(false));
        this.toast.error('Order creation failed');
      },
    });

    this.subs.push(sub);
  }




  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}
